/**
 * Quiz timing utilities based on quiz difficulty level
 */

/**
 * Get question time in seconds based on quiz level
 * @param {string} level - Quiz difficulty level ('Elementary', 'Moderate', 'Advance')
 * @returns {number} - Time in seconds per question
 */
export const getQuestionTimeByLevel = (level) => {
  switch (level) {
    case 'Advance':
      return 120; // 2 minutes for Advanced level
    case 'Elementary':
    case 'Moderate':
    default:
      return 60; // 1 minute for Elementary and Moderate levels
  }
};

// JEE-Main custom quizzes run at the real paper's pace: 2.4 minutes per
// question (75 questions in 3 hours). Keep in sync with
// get_question_time_seconds() in the API's Quiz controller.
const JEE_MAIN_EXAM_ID = 1;
const JEE_MAIN_QUESTION_TIME = 144;

/**
 * Get question time in seconds for a quiz, given the quiz record from the API.
 * JEE-Main custom (regular) quizzes use a fixed 2.4 minutes per question;
 * everything else is timed by level.
 * @param {object} quiz - Quiz record ({ exam_id, quiz_question_type, level })
 * @returns {number} - Time in seconds per question
 */
export const getQuizQuestionTime = (quiz) => {
  if (
    Number(quiz?.exam_id) === JEE_MAIN_EXAM_ID &&
    (quiz?.quiz_question_type || 'regular') === 'regular'
  ) {
    return JEE_MAIN_QUESTION_TIME;
  }
  return getQuestionTimeByLevel(quiz?.level || 'Elementary');
};

/**
 * Get question time in minutes (for display purposes)
 * @param {string} level - Quiz difficulty level
 * @returns {number} - Time in minutes per question
 */
export const getQuestionTimeInMinutes = (level) => {
  return getQuestionTimeByLevel(level) / 60;
};

/**
 * Calculate total quiz time based on number of questions and level
 * @param {number} questionCount - Number of questions in the quiz
 * @param {string} level - Quiz difficulty level
 * @returns {number} - Total time in seconds
 */
export const calculateTotalQuizTime = (questionCount, level) => {
  return questionCount * getQuestionTimeByLevel(level);
};

/**
 * Format time in seconds to MM:SS format
 * @param {number} seconds - Time in seconds
 * @returns {string} - Formatted time string
 */
export const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Get timing information for display
 * @param {string} level - Quiz difficulty level
 * @returns {object} - Object with timing information
 */
export const getTimingInfo = (level) => {
  const timePerQuestion = getQuestionTimeByLevel(level);
  const timeInMinutes = getQuestionTimeInMinutes(level);
  
  return {
    timePerQuestion, // seconds
    timeInMinutes,   // minutes
    levelDescription: level === 'Advance' ? 'Advanced (2 min/question)' : `${level} (1 min/question)`,
    displayText: `${timeInMinutes} minute${timeInMinutes !== 1 ? 's' : ''} per question`
  };
};
